import { useReducer } from "react";
import styles from "./ModalGeofence.module.css";
import useAppSnackbar from "../../../../hooks/useAppSnackbar";
import { geofenceService } from "../../../../services/guide/geofence-service";
import { CreateGeofence } from "../../../../types/api/guide/geofence.types";
import { mapCoordinatesToGeoPoints } from "../../../../utils/mapCoordinate";
import { useFetchAllGroupGeofence } from "../../../../hooks/guide/useFetchAllGroupGeofence";
import { InputLabel, Select } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";

type ModalGeofenceProps = {
  open: boolean;
  coordinates: Array<[number, number]>;
  onClose: () => void;
  onSave?: (meta: { name: string; description: string }) => void;
};

const formatCoords = (coords: Array<[number, number]>) =>
  coords
    .map(([lat, lng], i) => `${i + 1}. ${lat.toFixed(6)}, ${lng.toFixed(6)}`)
    .join("\n");

interface CreateGeofenceState {
  name: string;
  description: string;
  groupGeofenceId: string;
}

type Action =
  | { type: "SET_FIELD"; name: keyof CreateGeofenceState; value: any }
  | { type: "SET_FIELDS"; fields: Partial<CreateGeofenceState> };

const createGeofenceReducer = (
  state: CreateGeofenceState,
  action: Action,
): CreateGeofenceState => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.name]: action.value } as CreateGeofenceState;
    case "SET_FIELDS":
      return { ...state, ...action.fields } as CreateGeofenceState;
  }
};

export const ShapeMetaModal = ({
  open,
  coordinates,
  onClose,
  onSave,
}: ModalGeofenceProps) => {
  const initializeState: CreateGeofenceState = {
    name: "",
    description: "",
    groupGeofenceId: "",
  };

  const { data: groupGeofences } = useFetchAllGroupGeofence();

  const { showSuccess, showWarning, showError, SnackbarComponent } =
    useAppSnackbar();
  const [state, dispatch] = useReducer(createGeofenceReducer, initializeState);

  if (!open) return null;

  const coordsText = formatCoords(coordinates);

  const handleValue = (e) => {
    dispatch({ type: "SET_FIELD", name: e.target.name, value: e.target.value });
  };

  const handleSave = async () => {
    try {
      if (state.name === "") {
        showWarning("Введите имя");
        return;
      }

      const geofence: CreateGeofence = {
        points: mapCoordinatesToGeoPoints(coordinates),
        name: state.name,
        description: state.description,
        groupGeofenceId: state.groupGeofenceId,
      };
      await geofenceService.create(geofence);
      showSuccess("Сохранение геозоны прошло успешно");

      // ГЛАВНОЕ: возвращаем метаданные
      if (onSave) {
        onSave({ name: state.name, description: state.description });
      }

      onClose();
    } catch (e) {
      showError(e?.response?.data);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Добавить геозону</h3>
          <button
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Координаты (по порядку)</div>
            <textarea
              className={styles.modalTextarea}
              value={coordsText}
              readOnly
            />
          </div>

          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Название</div>
            <input
              className={`${styles.modalInput}`}
              name={"name"}
              onChange={handleValue}
              placeholder="Введите название"
              required={true}
            />
          </div>

          <div className={styles.modalField}>
            <div className={styles.modalLabel}>Описание</div>
            <textarea
              className={styles.modalTextarea}
              name={"description"}
              onChange={handleValue}
              placeholder="Введите описание"
            />
          </div>
          <div className={styles.modalField}>
            <InputLabel>Группа геозон</InputLabel>
            <Select
              name="groupGeofenceId"
              value={state.groupGeofenceId}
              onChange={handleValue}
              renderValue={(selected) =>
                groupGeofences?.find((gg) => gg.id === selected)?.name ?? ""
              }
              MenuProps={{
                disablePortal: true,
                PaperProps: { sx: { zIndex: 20000 } },
              }}
            >
              {groupGeofences?.map((x) => (
                <MenuItem key={x.id} value={x.id}>
                  <ListItemText primary={x.name} />
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.modalButton} onClick={onClose}>
            Отмена
          </button>
          <button
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            onClick={() => handleSave()}
          >
            Сохранить
          </button>
        </div>
        <SnackbarComponent />
      </div>
    </div>
  );
};
